import { useEffect, useMemo, useState, useCallback } from "react";
import { Users as UsersIcon, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import CatalogList from "@/pages/admin/CatalogList";
import NearbyRetailers from "@/pages/admin/NearbyRetailers";
import BeatOutletsManager from "@/pages/admin/BeatOutletsManager";
import { DistributorForm, RetailerForm, BeatForm } from "@/pages/admin/DistributionForms";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { api, formatApiError } from "@/lib/api";

const DIST_FORMS = { distributors: DistributorForm };
const RET_FORMS = { retailers: RetailerForm };
const BEAT_FORMS = { beats: BeatForm };

// Polish Item 1 — Beats popover column.
// Renders "N beats" (or "No beats") as a chip; on click a popover lists each
// beat's code + name (bulk-resolved via a single $in query — see enrichItems).
function BeatsPopoverChip({ row, beatMap }) {
  const bids = row.beat_ids || [];
  const empty = bids.length === 0;
  const beats = bids.map((id) => beatMap?.[id]).filter(Boolean);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`dist-beats-chip-${row.id}`}
          className={
            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium border transition-colors " +
            (empty
              ? "border-neutral-200 bg-neutral-50 text-neutral-400 cursor-default"
              : "border-[#6D2C91]/30 bg-[#6D2C91]/10 text-[#6D2C91] hover:bg-[#6D2C91]/20")
          }
        >
          <MapPin className="h-3 w-3" />
          {empty ? "No beats" : `${bids.length} beat${bids.length !== 1 ? "s" : ""}`}
        </button>
      </PopoverTrigger>
      {!empty && (
        <PopoverContent
          side="bottom" align="start"
          className="w-64 p-2"
          data-testid={`dist-beats-popover-${row.id}`}
        >
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 px-1 pb-1.5">
            {beats.length} beat{beats.length !== 1 ? "s" : ""} · {row.name}
          </div>
          <ul className="space-y-0.5 max-h-56 overflow-y-auto">
            {bids.map((id) => {
              const b = beatMap?.[id];
              return (
                <li key={id}
                    className="flex items-center justify-between gap-2 px-2 py-1 rounded hover:bg-neutral-50"
                    data-testid={`dist-beats-popover-row-${id}`}>
                  <span className="text-[11px] font-mono text-neutral-500">
                    {b?.code || id.slice(0, 8)}
                  </span>
                  <span className="text-xs text-neutral-800 text-right truncate">
                    {b?.name || <span className="text-neutral-400 italic">unresolved</span>}
                  </span>
                </li>
              );
            })}
          </ul>
        </PopoverContent>
      )}
    </Popover>
  );
}

// Bulk-resolves beats for the visible distributors in ONE $in query.
// Returns { beatMap: { [beat_id]: { id, code, name } } }.
async function enrichDistributorsWithBeats(items) {
  const ids = Array.from(new Set(
    items.flatMap((d) => d.beat_ids || []).filter(Boolean),
  ));
  if (ids.length === 0) return { beatMap: {} };
  const params = { ids: ids.join(","), page_size: Math.min(200, ids.length) };
  try {
    const { data } = await api.get("/beats", { params });
    const beatMap = Object.fromEntries((data.items || []).map((b) => [b.id, b]));
    return { beatMap };
  } catch { return { beatMap: {} }; }
}

export function DistributorsPage() {
  return (
    <CatalogList
      title="Distributors"
      subtitle="Wholesale partners with GPS, GST and payment terms"
      masterKey="distributors"
      apiPath="/distributors"
      entityType="distributors"
      pageSize={20}
      formsMap={DIST_FORMS}
      editForm={DistributorForm}
      searchPlaceholder="Search by code / name / phone / GST…"
      enrichItems={enrichDistributorsWithBeats}
      columns={[
        { key: "code", label: "Code" },
        { key: "name", label: "Name" },
        { key: "contact_person", label: "Contact" },
        { key: "phone", label: "Phone" },
        { key: "gst_no", label: "GST" },
        { key: "credit_limit", label: "Credit limit",
          render: (r) => "₹ " + (r.credit_limit ?? 0).toLocaleString() },
        // Drop 9-Hotfix — surface joined geo codes.
        { key: "zone_code", label: "Zone",
          render: (r) => r.zone_code || "—" },
        { key: "city_name", label: "City",
          render: (r) => r.city_name || "—" },
        // Area column dropped — this org's distributor naming convention
        // already embeds the area (e.g. "ANKIT TRADERS # DHARI"), so a
        // separate Area column was showing the same thing twice. The
        // underlying area_id field/requirement is untouched — only this
        // list's display changed.
        // Polish Item 1 — Beats popover column.
        { key: "beats", label: "Beats",
          render: (r, enrichment) => (
            <BeatsPopoverChip row={r} beatMap={enrichment?.beatMap || {}} />
          ) },
        { key: "is_active", label: "Status",
          render: (r) => r.is_active === false ? "Inactive" : "Active" },
      ]}
    />
  );
}

// --------------------------------------------------------------------------
// RetailerFilters — Zone + Outlet Type + Status combinable filters (UAT RC4).
// --------------------------------------------------------------------------
function RetailerFilters({ value, onChange }) {
  const [zones, setZones] = useState([]);
  const [outletTypes, setOutletTypes] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [z, ot] = await Promise.all([
          api.get("/zones"),
          api.get("/lookups", { params: { category: "outlet_type", page_size: 100 } }),
        ]);
        if (!alive) return;
        setZones((z.data.items || []).map(x => ({ id: x.id, code: x.code, name: x.name })));
        setOutletTypes((ot.data.items || []).map(x => ({ id: x.id, code: x.code, name: x.label })));
      } catch (_e) { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);
  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="w-44" data-testid="retailer-filter-zone-wrap">
        <Label className="text-[11px] uppercase tracking-widest text-neutral-500">Zone</Label>
        <SearchableSelect
          value={value.zone_id || ""}
          onChange={(v) => onChange({ ...value, zone_id: v || undefined })}
          options={zones}
          placeholder="Any zone"
          testid="retailer-filter-zone"
        />
      </div>
      <div className="w-52" data-testid="retailer-filter-otype-wrap">
        <Label className="text-[11px] uppercase tracking-widest text-neutral-500">Outlet type</Label>
        <SearchableSelect
          value={value.outlet_type_lookup_id || ""}
          onChange={(v) => onChange({ ...value, outlet_type_lookup_id: v || undefined })}
          options={outletTypes}
          placeholder="Any outlet type"
          testid="retailer-filter-otype"
        />
      </div>
    </div>
  );
}

export function RetailersPage() {
  const [filters, setFilters] = useState({}); // { zone_id?, outlet_type_lookup_id? }
  return (
    <div className="space-y-4">
      <div className="p-6 pb-0">
        <NearbyRetailers />
      </div>
      <CatalogList
        title="Retailers / Outlets"
        subtitle="Stores served by the distribution network"
        masterKey="retailers"
        apiPath="/retailers"
        entityType="retailers_outlets"
        pageSize={20}
        formsMap={RET_FORMS}
        editForm={RetailerForm}
        serverFilters={filters}
        extraFilters={<RetailerFilters value={filters} onChange={setFilters} />}
        searchPlaceholder="Search by code / name / phone / owner…"
        columns={[
          { key: "code", label: "Code" },
          { key: "name", label: "Name" },
          { key: "owner_name", label: "Owner" },
          { key: "phone", label: "Phone" },
          // Drop 9-Hotfix — joined geo + beat + distributor codes.
          { key: "zone_code", label: "Zone",
            render: (r) => r.zone_code || "—" },
          { key: "city_name", label: "City",
            render: (r) => r.city_name || "—" },
          { key: "beat_code", label: "Beat",
            render: (r) => r.beat_code || "—" },
          { key: "distributor_code", label: "Distributor",
            render: (r) => r.distributor_code || "—" },
          { key: "credit_days", label: "Credit d",
            render: (r) => r.credit_days ?? 0 },
          { key: "gps", label: "GPS",
            render: (r) => r.gps_lat && r.gps_lng
              ? `${r.gps_lat.toFixed(3)}, ${r.gps_lng.toFixed(3)}`
              : "—" },
          { key: "is_active", label: "Status",
            render: (r) => r.is_active === false ? "Inactive" : "Active" },
        ]}
      />
    </div>
  );
}

// --------------------------------------------------------------------------
// Beat bulk-reassign dialog (UAT RC2c). One PATCH per beat → one audit_log
// row per beat (matches existing single-beat contract).
// --------------------------------------------------------------------------
function BeatBulkReassignDialog({ open, onOpenChange, beatIds, onDone }) {
  const [ses, setSes] = useState([]);
  const [seId, setSeId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/users", { params: { role_code: "SE", page_size: 200 } })
      .then(({ data }) => setSes((data.items || []).map(u => ({
        id: u.id, code: u.phone || u.email || "", name: u.name,
      }))))
      .catch(() => {});
  }, [open]);
  const run = async () => {
    if (!seId || !beatIds?.length) return;
    setBusy(true);
    let ok = 0, failed = 0;
    for (const bid of beatIds) {
      try {
        await api.patch(`/beats/${bid}`, {
          assigned_se_user_id: seId,
          reason: `Bulk reassign to ${seId}`,
        });
        ok += 1;
      } catch (_e) { failed += 1; }
    }
    setBusy(false);
    if (failed === 0) toast.success(`Reassigned ${ok} beat${ok === 1 ? "" : "s"}`);
    else toast.error(`Reassigned ${ok}, failed ${failed}. Check audit log.`);
    onOpenChange(false);
    onDone?.();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="beat-bulk-reassign-dialog">
        <DialogHeader>
          <DialogTitle>Bulk reassign SE</DialogTitle>
          <DialogDescription>
            {beatIds?.length || 0} beat{(beatIds?.length || 0) === 1 ? "" : "s"} will be moved to the selected SE.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-neutral-500">Target SE</Label>
            <div className="mt-1">
              <SearchableSelect
                value={seId} onChange={setSeId} options={ses}
                placeholder="Search SE by name or phone…"
                testid="beat-bulk-reassign-se"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            disabled={busy || !seId || !beatIds?.length}
            onClick={run}
            className="tulsi-gradient text-white font-semibold"
            data-testid="beat-bulk-reassign-confirm"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Reassign {beatIds?.length || 0}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BeatsPage() {
  const [manageBeat, setManageBeat] = useState(null);
  const [bulk, setBulk] = useState(null); // { ids: [...] } while confirm dialog open
  return (
    <>
      <CatalogList
        title="Beats / Routes"
        subtitle="Weekly SE routes with outlet assignments"
        masterKey="beats"
        apiPath="/beats"
        entityType="beats"
        pageSize={20}
        formsMap={BEAT_FORMS}
        editForm={BeatForm}
        searchPlaceholder="Search by code / name…"
        bulkActionsRenderer={(rows, selectedIds, setSelectedIds) => (
          <div className="flex items-center justify-between rounded-xl border border-[#E5197F]/20 bg-[#E5197F]/5 px-4 py-2">
            <div className="text-sm text-neutral-700">
              <span className="font-semibold" data-testid="beats-bulkbar-count">{selectedIds.length}</span> selected
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setSelectedIds([])}
                      data-testid="beats-bulkbar-clear">Clear</Button>
              <Button size="sm"
                      className="tulsi-gradient text-white font-semibold"
                      onClick={() => setBulk({ ids: selectedIds })}
                      data-testid="beats-bulkbar-reassign">
                Reassign SE for {selectedIds.length}
              </Button>
            </div>
          </div>
        )}
        columns={[
          { key: "code", label: "Code" },
          { key: "name", label: "Name" },
          // The distributor serving the beat (a distributor is its own area, so no Area column).
          { key: "distributor_name", label: "Distributor",
            render: (r) => r.distributor_name || "—" },
          { key: "zone_code", label: "Zone",
            render: (r) => r.zone_code || "—" },
          // No Assigned SE / Day columns: a beat is linked only to its distributor; users
          // mapped to that distributor get the beat automatically.
          { key: "is_active", label: "Status",
            render: (r) => r.is_active === false ? "Inactive" : "Active" },
          { key: "manage", label: "Outlets",
            render: (r) => (
              <Button size="sm" variant="outline"
                      onClick={(e) => { e.stopPropagation(); setManageBeat(r); }}
                      data-testid={`beat-manage-${r.id}`}>
                <UsersIcon className="h-3 w-3 mr-1" /> Manage
              </Button>
            ) },
        ]}
      />
      <BeatOutletsManager beat={manageBeat} onOpenChange={setManageBeat} />
      <BeatBulkReassignDialog
        open={!!bulk} onOpenChange={(v) => !v && setBulk(null)}
        beatIds={bulk?.ids || []}
        onDone={() => window.location.reload()}
      />
    </>
  );
}
